import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { QuizAction } from '@/types/quiz';
import { QuizActionDialog } from './QuizActionDialog';
import { Plus, Trash2, Edit, ExternalLink, Webhook, GitBranch } from 'lucide-react';

interface QuizActionManagerProps {
  actions: QuizAction[];
  onChange: (actions: QuizAction[]) => void;
}

export function QuizActionManager({ actions, onChange }: QuizActionManagerProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAction, setEditingAction] = useState<QuizAction | undefined>();

  const handleAddAction = () => {
    setEditingAction(undefined);
    setDialogOpen(true);
  };

  const handleEditAction = (action: QuizAction) => {
    setEditingAction(action);
    setDialogOpen(true);
  };

  const handleDeleteAction = (actionId: string) => {
    onChange(actions.filter(a => a.id !== actionId));
  };

  const handleSaveAction = (action: QuizAction) => {
    if (editingAction) {
      onChange(actions.map(a => a.id === action.id ? action : a));
    } else {
      onChange([...actions, action]);
    }
  };

  const getActionIcon = (type: QuizAction['type']) => {
    switch (type) {
      case 'static-redirect':
        return <ExternalLink className="h-4 w-4" />;
      case 'webhook':
        return <Webhook className="h-4 w-4" />;
      case 'dynamic-redirect':
        return <GitBranch className="h-4 w-4" />;
    }
  };

  const getActionBadge = (type: QuizAction['type']) => {
    const labels = {
      'static-redirect': 'Static Redirect',
      'webhook': 'Webhook',
      'dynamic-redirect': 'Dynamic Redirect',
    };
    return labels[type];
  };

  const getActionSummary = (action: QuizAction) => {
    switch (action.type) {
      case 'static-redirect':
        return action.url;
      case 'webhook':
        return `${action.httpMethod} ${action.webhookUrl}`;
      case 'dynamic-redirect':
        return `${action.redirects.length} redirect${action.redirects.length !== 1 ? 's' : ''}`;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Post-Submission Actions</h3>
          <p className="text-sm text-muted-foreground">
            Configure what happens after users complete the quiz
          </p>
        </div>
        <Button type="button" onClick={handleAddAction}>
          <Plus className="h-4 w-4 mr-2" />
          Add Action
        </Button>
      </div>

      {actions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground text-center mb-4">
              No actions configured yet. Add an action to define what happens after quiz submission.
            </p>
            <Button type="button" variant="outline" onClick={handleAddAction}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Action
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {actions.map((action, index) => (
            <Card key={action.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="mt-1">{getActionIcon(action.type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="secondary">
                          {getActionBadge(action.type)}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          Order: {index + 1}
                        </span>
                      </div>
                      <h4 className="font-medium">{action.name}</h4>
                      <p className="text-sm text-muted-foreground truncate">
                        {getActionSummary(action)}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditAction(action)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteAction(action.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <QuizActionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        action={editingAction}
        onSave={handleSaveAction}
      />
    </div>
  );
}
